import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ErrorMessage, Form, Formik } from 'formik';
import * as Yup from 'yup';

import backArrow from '../../../assets/images/backarrow.svg';
import BoonLogo from '../../../assets/images/boon-logo.svg';
import BoonViewIcon from '../../../assets/images/BoonViewIcon.svg';
import NavigationBar from '../../../components/NavigationBar/NavigationBar';
import { OpeningSelect } from '../../../components/OpeningSelect/OpeningSelect';
import { OrganizationSelect } from '../../../components/OrganizationSelect/OrganizationSelect';
import { ReferralFlowStepper } from '../../../components/ReferralFlowStepper/ReferralFlowStepper';
import { ReferralProcess } from '../../../components/ReferralProcess/ReferralProcess';
import ButtonWithIcon from '../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import { CandidateInfoSection } from '../../../components/shared/CandidateInfoSection/CandidateInfoSection';
import Header from '../../../components/shared/Header/Header';
import { YourInfoSection } from '../../../components/shared/YourInfoSection/YourInfoSection';
import { useReferralFlowContext } from '../../../contexts/ReferralFlowContext';
import { IsMobile } from '../../../helpers/IsMobile';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { selectCurrentOrganization } from '../../../selectors/organizationSelectors';

import styles from './OpeningInfo.module.scss';

const createValidationSchema = (t) =>
  Yup.object({
    company: Yup.number().required(t('validation:company.required')),
    opening: Yup.number().required(t('validation:opening.required')),
  });

export const OpeningInfo = () => {
  const { t } = useTranslation('referral');
  const validationSchema = createValidationSchema(t);
  const { companyName } = useParams();
  const { isAuthenticated } = useCurrentUser();
  const [, setLocalOrganizationId] = useState(null);
  const {
    jobInfo,
    candidateInfo,
    personalInfo,
    setJobInfo,
    setCurrentStep,
    setOrganizationId,
    referralFormPermissions,
  } = useReferralFlowContext();
  const currentOrganization = useSelector(selectCurrentOrganization);
  const isMobile = IsMobile();

  return (
    <div className="flex w-full">
      {isAuthenticated && !companyName && <NavigationBar />}
      <div className={styles.outerContainer}>
        <Header
          isPublic={!isAuthenticated || companyName}
          title={t('headerTitle')}
          backButton={isAuthenticated && !companyName ? t('common:back') : ''}
          logo={isMobile ? BoonViewIcon : BoonLogo}
          logoClassName={isMobile ? 'w-full h-full' : 'w-full h-[20px] rounded-none'}
        />
        <div className={styles.mainContainer}>
          <ReferralProcess />
          <div className={styles.container}>
          <ReferralFlowStepper currentStep={3} />

          <div className={styles.formSection}>
            <div className={styles.formHeader}>
              <h2>{t('sendAReferral')}</h2>
            </div>

            <div className={styles.previousInfo}>
              <YourInfoSection personalInfo={personalInfo} setCurrentStep={setCurrentStep} />
              <CandidateInfoSection
                candidateInfo={candidateInfo}
                setCurrentStep={setCurrentStep}
                customQuestions={referralFormPermissions.referral_form_custom_questions || []}
              />
            </div>

            <Formik
              initialValues={{
                company: jobInfo?.company || '',
                opening: jobInfo?.opening || '',
              }}
              validationSchema={validationSchema}
              enableReinitialize
              onSubmit={(values) => {
                setJobInfo(values);
                setCurrentStep(4);
              }}
            >
              {({ setFieldValue, values, errors }) => (
                <Form>
                  <div className={styles.form}>
                    {(!isAuthenticated || (isAuthenticated && currentOrganization?.name === 'Boon')) &&
                      !companyName && (
                        <div className="space-y-[6px]">
                          <label htmlFor="company" className={styles.formLabel}>
                            {t('company')}
                          </label>
                          <OrganizationSelect
                            value={values.company}
                            onChange={(selectedOption) => {
                              setFieldValue('company', selectedOption ? selectedOption.value : '');
                              setLocalOrganizationId(selectedOption ? selectedOption.value : '');
                              setOrganizationId(selectedOption ? selectedOption.value : '');
                            }}
                            placeholder={t('selectCompany')}
                            isDisabled={!!companyName}
                            hasError={!!errors.company}
                          />
                          <ErrorMessage name="company" component="div" className={styles.errorMessage} />
                        </div>
                      )}
                    <div className="space-y-[6px]">
                      <label htmlFor="opening" className={styles.formLabel}>
                        {t('openingInfo')}
                      </label>
                      <OpeningSelect
                        organizationId={values.company}
                        value={values.opening}
                        onChange={(selectedOption) => {
                          setFieldValue('opening', selectedOption ? selectedOption.value : '');
                        }}
                        placeholder={t('selectOpening')}
                        hasError={!!errors.opening}
                      />
                      <ErrorMessage name="opening" component="div" className={styles.errorMessage} />
                    </div>
                  </div>
                  <div className={styles.nextButtonContainer}>
                    <ButtonWithIcon
                      img={backArrow}
                      type="button"
                      variant="tertiary-36"
                      text={t('common:back')}
                      textClassName="text-[#314155]"
                      containerClass="w-[142px]"
                      onClick={() => setCurrentStep(2)}
                    />
                    <ButtonWithIcon
                      data-testid="next-button"
                      type="submit"
                      variant="primary-36"
                      text={t('common:next')}
                      textClassName="text-white"
                      containerClass="w-[142px]"
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};
